import { PI_PRODUCTS } from '@/components/product-insights/pi-kit';
import { ApiError } from '@/lib/api';

/**
 * Shared wording of the SEED platform Company mapper (owner 2026-10-25, M1–M8):
 * the Catalog queue (`CompanyMapperPanel`), the company page section
 * (`CompanyMappingSection`) and the link dialog (`LinkPlatformCompanyDialog`).
 * One copy of each label so the three surfaces say the same thing.
 */

export type PlatformEntityType = 'school' | 'university' | 'partner' | null;

const ENTITY_LABELS: Record<'school' | 'university' | 'partner', string> = {
  school: 'School',
  university: 'University',
  partner: 'Partner',
};

export function entityTypeLabel(entityType: PlatformEntityType | undefined): string | null {
  if (entityType === null || entityType === undefined) return null;
  return ENTITY_LABELS[entityType] ?? entityType;
}

/** A programme key from the directory ('events', 'school-profiles' …) as its Product Insights name. */
export function productLabel(key: string): string {
  // Not a product: the server's website-and-country lookup source.
  if (key === 'school-lookup') return 'Website and country';
  return PI_PRODUCTS.find((product) => product.key === key)?.label ?? key;
}

export function productList(keys: readonly string[]): string {
  return keys.map(productLabel).join(', ');
}

/** The server's message for a refused write, matched on `code` first. */
export function mapperErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'COMPANY_NAME_NOT_PERMITTED':
        return 'Your permission set does not let you rename companies. The link was not made.';
      case 'PLATFORM_COMPANY_UNKNOWN':
        return 'The SEED platform no longer lists this record. The link was not made.';
      case 'PLATFORM_LINK_COMPANY_NOT_FOUND':
        return 'That company no longer exists. The link was not made.';
      case 'PLATFORM_LINK_NOT_FOUND':
        return 'This record is no longer linked.';
      default:
        break;
    }
  }
  return error instanceof Error && error.message.trim() !== '' ? error.message : fallback;
}
